/**
 * Connected admin dashboard with data fetching.
 *
 * Bundles hooks and UI for use in runtime screens.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { createUsersApi } from "@animus/frontend-features/admin/users";
import {
  useApiConfig,
  useAuthStrategy,
} from "@animus/frontend-features/config";
import { userCheck, userCross, users as usersIcon } from "@animus/iconic-svg";

import { AdminDashboard, type DashboardStat } from "./admin-dashboard.tsx";

function AdminDashboardConnected() {
  const { baseUrl } = useApiConfig();
  const auth = useAuthStrategy();
  const api = useMemo(() => createUsersApi(baseUrl, auth), [baseUrl, auth]);

  const [stats, setStats] = useState<DashboardStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await api.listUsers({ limit: 100 });

    if (result.isErr) {
      setError(result.error.message ?? "Failed to fetch users");
      setIsLoading(false);
      return;
    }

    const users = result.value.users;
    const totalUsers = users.length;
    const bannedUsers = users.filter((u) => u.banned).length;

    setStats([
      {
        title: "Total Users",
        value: totalUsers,
        color: "blue",
        icon: usersIcon,
      },
      {
        title: "Banned Users",
        value: bannedUsers,
        color: "red",
        icon: userCross,
      },
      {
        title: "Active Users",
        value: totalUsers - bannedUsers,
        color: "green",
        icon: userCheck,
      },
    ]);
    setIsLoading(false);
  }, [api]);

  useEffect(() => {
    fetchStats().catch(() => {
      // Error already handled in fetchStats
    });
  }, [fetchStats]);

  return <AdminDashboard error={error} isLoading={isLoading} stats={stats} />;
}

export { AdminDashboardConnected };
